import React from "react";
import {
  Box,
  Card,
  CardContent,
  IconButton,
  Radio,
  Tooltip,
  Typography,
} from "@mui/material";
import { Delete, Edit, Star, StarBorder } from "@mui/icons-material";
import { UserAddress } from "./controller";
import { maskCep } from "../../../../utils/masks";

interface AddressCardProps {
  address: UserAddress;
  onEdit: (address: UserAddress) => void;
  onDelete: (address: UserAddress) => void;
  onSetDefault: (address: UserAddress) => void;
  onSelect: (id: number) => void;
  selected?: boolean;
}

export const AddressCard: React.FC<AddressCardProps> = ({
  address,
  onEdit,
  onDelete,
  onSetDefault,
  onSelect,
  selected = false,
}) => {
  const addressLine = `${address.street}, ${address.number}`.trim();
  const completionLine = (address.completion ?? "").trim();
  const customLabel = (address.label ?? "").trim();
  const primaryHeading = customLabel || addressLine;
  const secondaryHeading = customLabel && customLabel !== addressLine
    ? addressLine
    : completionLine;

  const handleSelect = () => onSelect(address.id);
  const handleEdit = (event: React.MouseEvent) => {
    event.stopPropagation();
    onEdit(address);
  };
  const handleDelete = (event: React.MouseEvent) => {
    event.stopPropagation();
    onDelete(address);
  };
  const handleSetDefault = (event: React.MouseEvent) => {
    event.stopPropagation();
    onSetDefault(address);
  };

  return (
    <Card
      elevation={0}
      onClick={handleSelect}
      sx={{
        position: "relative",
        borderRadius: 3,
        border: "1px solid",
        borderColor: selected ? "primary.main" : "divider",
        height: '100%',
        transition: "all 0.25s ease",

        cursor: "pointer",
        "&:hover": {
          boxShadow: (theme) => `0 0 0 3px ${theme.palette.primary.main}11`,
        },
      }}
    >

      <CardContent sx={{
        display: "flex", flexDirection: 'column', gap: 0.5, px: 2, py: 1
      }}>
        <Box display={'flex'} alignItems={'center'} justifyContent={'space-between'}>
          <Radio
            checked={selected}
            onChange={handleSelect}
            value={address.id}
            color="primary"
            size="small"
          />
          <Box display="flex" gap={0.5}>
            <Tooltip title="Editar">
              <IconButton size="small" onClick={handleEdit}>
                <Edit fontSize="inherit" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Excluir">
              <IconButton size="small" color="error" onClick={handleDelete}>
                <Delete fontSize="inherit" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        <Box width={'100%'} display="flex" flexDirection={'column'} alignItems="flex-start" gap={0.75}>
          <Typography fontWeight={700} display={'flex'} alignItems={'center'} gap={0.5}>
            {primaryHeading}
            <Tooltip title={address.isDefault ? "Endereço padrão" : "Definir como padrão"}>
              <IconButton
                size="small"
                color={address.isDefault ? "warning" : "default"}
                onClick={handleSetDefault}
              >
                {address.isDefault ? <Star fontSize="inherit" /> : <StarBorder fontSize="inherit" />}
              </IconButton>
            </Tooltip>
          </Typography>

          {secondaryHeading && (
            <Typography variant="body2" color="text.secondary">
              {secondaryHeading}
            </Typography>
          )}
          <Typography variant="body2" color="text.secondary">
            {`${address.neighborhood} - ${address.city}/${address.state}`}
          </Typography>

          <Typography variant="caption" color="text.secondary">
            CEP: {maskCep(address.postalCode)}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

export default AddressCard;
